import React, { useState, useEffect, useRef } from "react";
import {
    Box, Button, Typography, Paper, CircularProgress, Alert,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions, useTheme
} from "@mui/material";
import { useEventsApi } from "../hooks/useEventsApi";
import { config } from "../config";
import ImportProgressBar from "../components/ImportProgressBar";
import { supportedEventLanguages } from "../constants/languages";
import { formatLocalDateTime } from "../utils/formatLocalDateTime";

const ImportPage: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [jobId, setJobId] = useState<number | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [importErrors, setImportErrors] = useState<any[]>([]);
    const [jobs, setJobs] = useState<any[]>([]);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const { callApi } = useEventsApi();
    const theme = useTheme();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] ?? null;
        setFile(selectedFile);
        setResult(null);
        setError(null);
        setJobId(null);
        setStatus(null);
        setImportErrors([]);
    };

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true);
        setError(null);
        setResult(null);
        setStatus(null);

        const formData = new FormData();
        const isCsv = file.name.endsWith(".csv");
        const endpoint = isCsv ? "/api/import-csv/" : "/api/import-edf/";
        formData.append("file", file);
        formData.append("language", language);

        try {
            const response = await callApi(`${config.apiUrl}${endpoint}`, "POST", formData, true);
            setJobId(response.job_id);
            setStatus("pending");
        } catch (err: any) {
            setError("Fehler beim Hochladen der Datei.");
        } finally {
            setLoading(false);
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = ""; // Reset input so same file can be reselected
            }
        }

    };

    const [jobProgress, setJobProgress] = useState<number | null>(null);
    const [jobTotal, setJobTotal] = useState<number | null>(null);

    useEffect(() => {
        if (!jobId) return;

        const interval = setInterval(async () => {
            try {
                const jobStatus = await callApi(`${config.apiUrl}/api/import-status/${jobId}/`, "GET");

                setStatus(jobStatus.status);
                setJobProgress(jobStatus.progress); // z. B. 45
                setJobTotal(jobStatus.total);       // z. B. 100

                if (jobStatus.status === "done") {
                    setResult(jobStatus.result);
                    clearInterval(interval);
                    const errorResponse = await callApi(`${config.apiUrl}/api/import-errors/${jobId}/`, "GET");
                    setImportErrors(errorResponse);
                } else if (jobStatus.status === "error") {
                    setError(jobStatus.error_message ?? "Unbekannter Fehler beim Import.");
                    clearInterval(interval);
                }
            } catch (err) {
                setError("Fehler beim Abrufen des Import-Status.");
                clearInterval(interval);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [jobId]);

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const response = await callApi(`${config.apiUrl}/api/import-jobs/`, "GET");
                setJobs(response);
            } catch (err) {
                console.error("Fehler beim Laden der Import-Jobs.");
            }
        };
        fetchJobs();
        const intervalId = setInterval(fetchJobs, 3000);
        return () => clearInterval(intervalId);
    }, []);

    const handleJobSelect = async (id: number) => {
        setJobId(id);
        setResult(null);
        setImportErrors([]);
        setError(null);
        setStatus("loading");
        try {
            const jobDetails = await callApi(`${config.apiUrl}/api/import-jobs/${id}/details/`, "GET");
            setStatus(jobDetails.status);
            setResult(jobDetails.result);
            setImportErrors(jobDetails.errors ?? []);
            setShowErrorDialog(true);
        } catch (err) {
            setError("Fehler beim Laden der Jobdetails.");
        }
    };

    const [language, setLanguage] = useState("eng");
    const fileInputRef = useRef<HTMLInputElement>(null);

    return (
        <Box p={4}>
            <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Event Import (CSV & EDF)
            </Typography>
            <Paper sx={{ p: 3, mb: 2, mt: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <label htmlFor="file-upload">
                        <input
                            ref={fileInputRef}
                            id="file-upload"
                            type="file"
                            accept=".csv,.edf"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                        <Button variant="outlined" component="span">Select file</Button>
                    </label>
                    {file && (
                        <>
                            <Typography variant="body2">{file.name}</Typography>
                            <Typography variant="body2">
                                Format: {file.name.endsWith(".csv") ? "CSV" : "EDF (XML)"}
                            </Typography>
                            <Button variant="text" color="secondary" onClick={() => {
                                setFile(null);
                                if (fileInputRef.current) {
                                    fileInputRef.current.value = ""; // Reset input so same file can be reselected
                                }
                            }}>
                                Clear
                            </Button>
                        </>
                    )}

                    {file?.name.endsWith(".edf") && (
                        <Box>
                            <Typography variant="body2">Description language:</Typography>
                            <select
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                style={{ padding: "8px", marginTop: "8px", marginBottom: "16px" }}
                            >
                                {supportedEventLanguages.map((code) => (
                                    <option key={code} value={code}>{code.toUpperCase()} ({code})</option>
                                ))}
                            </select>
                        </Box>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        disabled={!file || loading || status === "pending" || status === "running"}
                    >
                        {loading ? <CircularProgress size={24} /> : "Upload"}
                    </Button>
                </Box>
                <Typography sx={{ color: theme.palette.text.primary, mt: 2 }}>Note: Imported events will be marked as "approved" automatically.</Typography>
            </Paper>

            <Box>
                {status === "pending" && <Alert severity="info">Preparing import...</Alert>}
                {status === "running" && <Alert severity="info">Import in progress...</Alert>}
                {status === "running" && jobProgress !== null && jobTotal !== null && (
                    <ImportProgressBar progress={jobProgress} total={jobTotal} />
                )}
                {status === "error" && error && <Alert severity="error">{error}</Alert>}
                {status === "done" && result && !showErrorDialog && (
                    <Box mt={2}>
                        <Alert severity="success">
                            {result.imported} Rows imported, {result.updated} updated, {result.skipped} skipped.
                        </Alert>
                        {importErrors.length > 0 && (
                            <Box mt={2}>
                                <Button variant="outlined" onClick={() => setShowErrorDialog(true)}>
                                    Show report
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            <Box mt={4}>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                    Past Import Jobs
                </Typography>
                <TableContainer sx={{ maxHeight: "30vh", overflowY: "auto" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created at</TableCell>
                                <TableCell>Progress</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.map((job) => (
                                <TableRow key={job.id}>
                                    <TableCell>{job.id}</TableCell>
                                    <TableCell>{job.status}</TableCell>
                                    <TableCell>{formatLocalDateTime(job.created_at)}</TableCell>
                                    <TableCell>{job.progress}</TableCell>
                                    <TableCell>{job.total}</TableCell>
                                    <TableCell>
                                        <Button size="small" onClick={() => handleJobSelect(job.id)}>
                                            Show report
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Import report</DialogTitle>
                <DialogContent dividers>
                    <TableContainer sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Row</TableCell>
                                    <TableCell>EventCode</TableCell>
                                    <TableCell>Message</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {importErrors.map((err, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{err.row_number}</TableCell>
                                        <TableCell>{err.event_code ?? "-"}</TableCell>
                                        <TableCell>{err.reason}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImportPage;